// @ts-nocheck
import axios from "@/helpers/interceptors";
import { B2bPortalRight } from "@/enums/B2bPortalRight";

/**
 * Mirrors B2B API RequirePermission on AccountsController, ClientController (display currency),
 * CurrencyExchangeController, and ForexController.
 */
function portalRights(rootState) {
  const r = rootState?.auth?.permissions?.rights;
  return Array.isArray(r) ? r : null;
}

function hasPortalRight(rootState, right) {
  return !!portalRights(rootState)?.includes(right);
}

const state = {
  accounts: [],
  activeAccount: null,
  isLoadingAccounts: false,
  isLoadingCurrencies: false,
  accountsFetched: false,
  currencies: [],
  sellCurrencies: [],
  buyCurrencies: [],
	currenciesCombination: null,
	currenciesCombinations: [],
  currenciesCombinationRate: [],
  isCurrenciesCombinationRateLoading: false,
  forexSettingsDefaultMinSumm: null,
};

const mutations = {
  SET_ACCOUNTS(state, payload) {
    state.accounts = payload;
    state.accountsFetched = true;
  },
  SET_ACCOUNTS_FETCHED(state, payload) {
    state.accountsFetched = payload;
  },
  SET_ACTIVE_ACCOUNT(state, payload) {
    state.activeAccount = payload;
  },
  SET_ACCOUNT_CURRENCIES(state, payload) {
    state.currencies = payload;
  },
  SET_SELL_CURRENCIES(state, payload) {
    state.sellCurrencies = payload;
  },
  SET_BUY_CURRENCIES(state, payload) {
    state.buyCurrencies = payload;
  },
	SET_CURRENCIES_COMBINATION(state, value) {
		state.currenciesCombination = value;
	},
	SET_CURRENCIES_COMBINATIONS(state, array) {
		state.currenciesCombinations = array;
	},
  SET_CURRENCIES_COMBINATION_RATE(state, payload) {
    state.currenciesCombinationRate = payload
  },
  SET_CURRENCIES_COMBINATION_LOADING_STATUS(state, payload) {
    state.isCurrenciesCombinationRateLoading = payload
  },
  SET_DEFAULT_FOREX_SETTINGS_MIN_SUMM(state, payload) {
    state.forexSettingsDefaultMinSumm = payload
  },
};

const actions = {

  /**
   * @name fetchAccounts
   * @description Get list of accounts
   * @param state
   * @param commit
   * @param dispatch
   */
  async fetchAccounts({
    state, commit, dispatch, rootState,
  }) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) {
      commit("SET_ACCOUNTS", []);
      return;
    }
    try {
      state.isLoadingAccounts = true;
      const { status, data } = await axios.get("/api/accounts");
      if (status === 200 && data.accounts) {
        commit("SET_ACCOUNTS", data.accounts);
        if (!state.currencies.length) {
          dispatch("fetchAccountCurrencies");
        }
      }
    } catch (e) {
      console.log(e);
      state.accountsFetched = true;
    } finally {
      state.isLoadingAccounts = false;
    }
  },
  async getAccounts({
    state, rootState,
  }) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) {
      return [];
    }
    try {
      state.isLoadingAccounts = true;
      const { status, data } = await axios.get("/api/accounts");
      if (status === 200 && data.accounts) {
        return data.accounts;
      }
    } catch (e) {
      console.log(e);
    } finally {
      state.isLoadingAccounts = false;
    }
  },
  /**
   * @name fetchAccountsByClientId
   * @description Get list of accounts for a specific merchant/clientId
   * @param state
   * @param commit
   * @param dispatch
   * @param clientId - The merchant clientId
   */
  async fetchAccountsByClientId({
    state, commit, dispatch, rootState,
  }, clientId) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) {
      commit("SET_ACCOUNTS", []);
      return;
    }
    try {
      state.isLoadingAccounts = true;
      // Try to fetch accounts by clientId - adjust the endpoint if your API uses a different path
      const { status, data } = await axios.get(`/api/accounts/${clientId}`);
      if (status === 200 && data.accounts) {
        commit("SET_ACCOUNTS", data.accounts);
        if (!state.currencies.length) {
          dispatch("fetchAccountCurrencies");
        }
      }
    } catch (e) {
      console.log('Error fetching accounts by clientId:', e);
      commit("SET_ACCOUNTS", []);
    } finally {
      state.isLoadingAccounts = false;
    }
  },

  /**
   * @name fetchAccountCurrencies
   * @description Get account currencies list
   * @param commit
   */
  async fetchAccountCurrencies({ commit, rootState }) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) {
      commit("SET_ACCOUNT_CURRENCIES", []);
      return;
    }
    try {
      state.isLoadingCurrencies = true;
      const { status, data } = await axios.get("/api/accounts/currencies");
      if (status === 200 && data.currencies) {
        console.log("fetchAccountCurrencies");
        commit("SET_ACCOUNT_CURRENCIES", data.currencies);
      }
    } catch (e) {
      console.log(e);
    } finally {
      state.isLoadingCurrencies = false;
    }
  },

  /**
   * @name createAccount
   * @description Create new account
   * @param commit
   * @param currency - string
   */
  // eslint-disable-next-line consistent-return
  async createAccount({ commit, rootState }, currency) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountAdd)) {
      return;
    }
    try {
      const { status, data } = await axios.post("/api/accounts", { currency });
      if (status === 200 && data.accounts) {
        commit("SET_ACCOUNTS", data.accounts);
      }
      return { status };
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name setDisplayCurrency
   * @description set display currency
   * @param dispatch (fetchAccounts) - to update changes in accounts
   * @param displayCurrency - displayCurrency of accounts
   */
  async setDisplayCurrency({ dispatch, rootState }, displayCurrency) {
    if (!hasPortalRight(rootState, B2bPortalRight.ProfileEdit)) {
      return;
    }
    try {
      const { status } = await axios.post("api/Client/change-display-currency", { displayCurrency });
      if (status === 200) {
        dispatch("fetchAccounts");
      }
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name setOrderOfAccount
   * @description set order of account
   * @param dispatch (fetchAccounts) - to update changes in accounts
   * @param data (number - string , splitOrder - number)
   */
  async setOrderOfAccount({ dispatch, rootState }, data) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountEdit)) {
      return;
    }
    try {
      const { status } = await axios.post("/api/accounts/set-order", data);
      if (status === 200) {
        dispatch("fetchAccounts");
      }
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name activateAccount
   * @description activate the account
   * @param commit (SET_ACCOUNTS) - to update current accounts
   * @param number - string
   */

  async activateAccount({ commit, rootState }, number) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountEdit)) {
      return;
    }
    try {
      const { status, data } = await axios.post("/api/accounts/activate", { number });
      if (status === 200) {
        commit("SET_ACCOUNTS", data.accounts);
      }
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name deactivateAccount
   * @description deactivate the account
   * @param dispatch (fetchAccounts) - to update changes in accounts
   * @param number - string
   */

  async deactivateAccount({ dispatch, rootState }, number) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountEdit)) {
      return;
    }
    try {
      const { status, data } = await axios.post("/api/accounts/deactivate", { number });
      if (status === 200 && data.value.success) {
        dispatch("fetchAccounts");
      }
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name fetchExchangeCurrencies
   * @description get buy and sell currencies
   * @param commit (SET_SELL_CURRENCIES) - set sell currencies
   * @param commit (SET_BUY_CURRENCIES) - set buy currencies
   */

  async fetchExchangeCurrencies({commit, dispatch, rootState}) {
    if (!hasPortalRight(rootState, B2bPortalRight.ForexView)) {
      commit("SET_SELL_CURRENCIES", []);
      commit("SET_BUY_CURRENCIES", []);
      return;
    }
    try {
      const { status, data } = await axios.get("/api/exchange/currencies");
      if (status === 200 && data.sellCurrencies && data.buyCurrencies) {
        commit("SET_SELL_CURRENCIES", data.sellCurrencies);
        commit("SET_BUY_CURRENCIES", data.buyCurrencies);
        dispatch('setCurrenciesCombinations', data.sellCurrencies)
      }
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name fetchExchangeRate
   * @description get exchange rate
   */

  // eslint-disable-next-line consistent-return
  async fetchExchangeRate({ rootState }, params) {
    if (!hasPortalRight(rootState, B2bPortalRight.ForexAdd)) {
      return;
    }
    try {
      const { status, data } = await axios.get("/api/exchange/rate", { params });
      if (status === 200) {
        return data;
      }
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * @name exchangeConvert
   * @description performs currency convert between client accounts
   */

  // eslint-disable-next-line consistent-return
  async exchangeConvert({ dispatch, rootState }, params) {
    if (!hasPortalRight(rootState, B2bPortalRight.ForexAdd)) {
      return;
    }
    try {
      const { data, status } = await axios.post("/api/exchange/convert", params, { params });
      if (status === 200 && data) {
        dispatch("fetchAccounts");
        dispatch("fetchExchangeCurrencies");
        return { data, status };
      }
    } catch (e) {
      console.log(e);
    }
  },

	// eslint-disable-next-line consistent-return
	async exchangeConvertForex({ dispatch, rootState }, params) {
		if (!hasPortalRight(rootState, B2bPortalRight.ForexAdd)) {
			return;
		}
		try {
			const { data, status } = await axios.post("/api/forex", params, { params });
			if (status === 200 && data) {
				dispatch("fetchAccounts");
				dispatch("fetchExchangeCurrencies");
				return { data, status };
			}
		} catch (e) {
			console.log(e);
		}
	},

  /**
   * @name fetchAccountDetails
   * @description get details of account
   */

  // eslint-disable-next-line consistent-return
  async fetchAccountDetails({ rootState }, accountNumber) {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) {
      return;
    }
    try {
      const { status, data } = await axios.get(`/api/accounts/v6/details/${accountNumber}`, {});
      if (status === 200 && data.requisites) {
        return data.requisites;
      }
    } catch (e) {
      console.log(e);
    }
  },


	setCurrenciesCombinations({ state, commit }, data) {
		const currencies  = []
		const combinations = [];

		//create array
		for (const i in data) {
			currencies.push(data[i].currency)
		}

		//create combinations
		for (let i = 0; i < currencies.length; i++) {
			for (let j = i + 1; j < currencies.length; j++) {
				const pair1 = {
					value: [currencies[i], currencies[j]],
					text: currencies[i] + ' / ' + currencies[j]
				};
				const pair2 = {
					value: [currencies[j], currencies[i]],
					text: currencies[j] + ' / ' + currencies[i]
				};
				combinations.push(pair1, pair2);
			}
		}

		if(!state.currenciesCombination)
			commit("SET_CURRENCIES_COMBINATION", combinations[0]?.value);

		commit('SET_CURRENCIES_COMBINATIONS', combinations)

	},

};

const getters = {
  /** Use instead of `state.accounts.currencies` so AccountView is enforced. */
  accountCurrencies: (state, _getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return [];
    return state.currencies || [];
  },
  accounts: (state, _getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return [];
    return state.accounts ? [...state.accounts].sort((a, b) => a.splitOrder - b.splitOrder) : [];
  },
  accountsFetched: (state) => state.accountsFetched,
  isNewUser: (state, _getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return false;
    return state.accounts.length === 0 && state.accountsFetched;
  },
  hasAccounts: (state, getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return false;
    return getters.accounts.length > 0;
  },
  displayCurrency: (state, getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return {};
    const currencies = getters.accountCurrencies;
    return currencies.length && getters.activeAccounts?.length
      ? currencies.find((currency) => currency.isoCode === getters.activeAccounts[0]?.displayCurrency) || {}
      : {};
  },
  activeAccounts: (state, getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return [];
    return getters.accounts?.filter((item) => item.status === "Active") || [];
  },
  inactiveAccounts: (state, getters, rootState) => {
    if (!hasPortalRight(rootState, B2bPortalRight.AccountView)) return [];
    return getters.accounts?.filter((item) => item.status === "NotActive") || [];
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
